import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { useQuery } from "@tanstack/react-query";
import { divIcon, type LatLngBoundsExpression, type LatLngExpression } from "leaflet";
import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { googleMapsLocationUrl } from "../lib/travel";
import type { MapProviderConfig } from "../lib/types";

export type ActualMapPoint = {
  id: string;
  name: string;
  latitude: string;
  longitude: string;
  number: number;
  kind?: string;
  markerType?: "stay" | "origin" | "destination" | "activity";
};

let googleMapsPromise: Promise<{
  maps: google.maps.MapsLibrary;
  marker: google.maps.MarkerLibrary;
}> | null = null;

function loadGoogleMaps(apiKey: string, mapId: string | null) {
  if (!googleMapsPromise) {
    setOptions({
      key: apiKey,
      v: "weekly",
      authReferrerPolicy: "origin",
      mapIds: mapId ? [mapId] : undefined,
    });
    googleMapsPromise = Promise.all([importLibrary("maps"), importLibrary("marker")])
      .then(([maps, marker]) => ({ maps, marker }));
  }
  return googleMapsPromise;
}

function latitudeLongitude(point: ActualMapPoint) {
  return { lat: Number(point.latitude), lng: Number(point.longitude) };
}

function leafletCoordinates(point: ActualMapPoint): LatLngExpression {
  const { lat, lng } = latitudeLongitude(point);
  return [lat, lng];
}

function markerLabel(point: ActualMapPoint) {
  return point.markerType === "stay" ? "⌂" : String(point.number);
}

function popupContent(point: ActualMapPoint) {
  const content = document.createElement("div");
  content.className = "map-popup-copy";

  const kind = document.createElement("small");
  kind.textContent = point.markerType === "stay" ? "MAPPED STAY" : point.kind || "ROUTE STOP";
  const name = document.createElement("strong");
  name.textContent = point.name;
  const link = document.createElement("a");
  link.href = googleMapsLocationUrl(point);
  link.target = "_blank";
  link.rel = "noreferrer";
  link.textContent = "Open in Google Maps ↗";
  content.append(kind, name, link);
  return content;
}

function GoogleMapCanvas({
  points,
  apiKey,
  mapId,
  onUnavailable,
}: {
  points: ActualMapPoint[];
  apiKey: string;
  mapId: string | null;
  onUnavailable: () => void;
}) {
  const canvas = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let cleanup = () => undefined;

    void loadGoogleMaps(apiKey, mapId).then(({ maps, marker }) => {
      if (cancelled || !canvas.current) return;
      const map = new maps.Map(canvas.current, {
        center: latitudeLongitude(points[0]),
        zoom: 13,
        mapId: mapId || undefined,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: "cooperative",
      });
      const infoWindow = new maps.InfoWindow();
      const route = points.map(latitudeLongitude);
      const polyline = route.length > 1 ? new google.maps.Polyline({
        map,
        path: route,
        strokeColor: "#d7684d",
        strokeOpacity: .82,
        strokeWeight: 4,
      }) : null;
      const mapMarkers: Array<google.maps.Marker | google.maps.marker.AdvancedMarkerElement> = [];

      points.forEach((point) => {
        let mapMarker: google.maps.Marker | google.maps.marker.AdvancedMarkerElement;
        if (mapId) {
          const content = document.createElement("span");
          content.className = `voyage-map-marker ${point.markerType || "activity"}`;
          content.textContent = markerLabel(point);
          mapMarker = new marker.AdvancedMarkerElement({
            map,
            position: latitudeLongitude(point),
            title: point.name,
            content,
          });
        } else {
          const isStay = point.markerType === "stay";
          mapMarker = new marker.Marker({
            map,
            position: latitudeLongitude(point),
            title: point.name,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: isStay ? "#dcebe4" : point.markerType === "destination" ? "#d7684d" : point.markerType === "origin" ? "#356f78" : "#10233f",
              fillOpacity: 1,
              strokeColor: "#fffdf8",
              strokeWeight: 3,
              scale: 16,
            },
            label: {
              text: markerLabel(point),
              color: isStay ? "#10233f" : "#ffffff",
              fontSize: isStay ? "15px" : "10px",
              fontWeight: "800",
            },
          });
        }
        mapMarker.addListener("click", () => {
          infoWindow.setContent(popupContent(point));
          infoWindow.open({ map, anchor: mapMarker });
        });
        mapMarkers.push(mapMarker);
      });

      if (points.length === 1) {
        map.setCenter(latitudeLongitude(points[0]));
        map.setZoom(15);
      } else {
        const bounds = new google.maps.LatLngBounds();
        route.forEach((position) => bounds.extend(position));
        map.fitBounds(bounds, 44);
        google.maps.event.addListenerOnce(map, "idle", () => {
          if ((map.getZoom() || 0) > 15) map.setZoom(15);
        });
      }

      cleanup = () => {
        infoWindow.close();
        polyline?.setMap(null);
        mapMarkers.forEach((item) => {
          if (item instanceof marker.AdvancedMarkerElement) item.map = null;
          else item.setMap(null);
        });
      };
    }).catch(() => {
      if (!cancelled) onUnavailable();
    });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [apiKey, mapId, onUnavailable, points]);

  return <div ref={canvas} className="actual-map-canvas google-map-canvas" aria-label="Interactive trip map" />;
}

function FitToRoute({ points }: { points: ActualMapPoint[] }) {
  const map = useMap();

  useEffect(() => {
    const resizeTimer = window.setTimeout(() => map.invalidateSize(), 80);
    if (points.length === 1) map.setView(leafletCoordinates(points[0]), 15, { animate: false });
    else if (points.length > 1) {
      map.fitBounds(points.map(leafletCoordinates) as LatLngBoundsExpression, {
        animate: false,
        maxZoom: 15,
        padding: [44, 44],
      });
    }
    return () => window.clearTimeout(resizeTimer);
  }, [map, points]);

  return null;
}

function OpenStreetMapCanvas({ points }: { points: ActualMapPoint[] }) {
  const center = leafletCoordinates(points[0]);
  const route = points.map(leafletCoordinates);
  const icons = useMemo(() => new Map(points.map((point) => [point.id, divIcon({
    className: "voyage-map-marker-shell",
    html: `<span class="voyage-map-marker ${point.markerType || "activity"}">${markerLabel(point)}</span>`,
    iconSize: [34, 40],
    iconAnchor: [17, 36],
    popupAnchor: [0, -34],
  })])), [points]);

  return <MapContainer center={center} zoom={13} scrollWheelZoom className="actual-map-canvas" aria-label="Interactive trip map">
    <TileLayer
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
      maxZoom={19}
    />
    {route.length > 1 && <Polyline positions={route} pathOptions={{ color: "#d7684d", weight: 4, opacity: .82, dashArray: "7 9" }} />}
    {points.map((point) => <Marker key={point.id} position={leafletCoordinates(point)} icon={icons.get(point.id)} title={point.name}>
      <Popup>
        <div className="map-popup-copy"><small>{point.markerType === "stay" ? "MAPPED STAY" : point.kind || "ROUTE STOP"}</small><strong>{point.name}</strong><a href={googleMapsLocationUrl(point)} target="_blank" rel="noreferrer">Open in Google Maps ↗</a></div>
      </Popup>
    </Marker>)}
    <FitToRoute points={points} />
  </MapContainer>;
}

export function ActualMap({ points, large = false }: { points: ActualMapPoint[]; large?: boolean }) {
  const [googleUnavailable, setGoogleUnavailable] = useState(false);
  const { data: config, isLoading } = useQuery({
    queryKey: ["map-provider-config"],
    queryFn: () => api<MapProviderConfig>("/maps/config"),
    staleTime: Infinity,
    retry: 0,
  });
  const useGoogle = config?.provider === "google" && Boolean(config.api_key) && !googleUnavailable;

  return <div
    className={`actual-map${large ? " large" : " compact"}`}
    data-testid="actual-map"
    data-map-provider={useGoogle ? "google" : isLoading ? "loading" : "openstreetmap"}
  >
    {isLoading
      ? <div className="actual-map-loading">Loading map…</div>
      : useGoogle
        ? <GoogleMapCanvas points={points} apiKey={config.api_key!} mapId={config.map_id} onUnavailable={() => setGoogleUnavailable(true)} />
        : <OpenStreetMapCanvas points={points} />}
    {!isLoading && <div className="actual-map-provider">{useGoogle ? "Google Maps" : "OpenStreetMap fallback"}</div>}
    <div className="actual-map-key" aria-label="Map marker key"><span><i className="stay">⌂</i>Stay</span><span><i>1</i>Route stop</span></div>
  </div>;
}
